import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AccountNumberState, InfoRequestForClient } from '@steward-app/steward-types';
import { ApiError, isConnectivityError, userMessage } from '../../api/client';
import {
  clearOnboardingAccountNumber, onboardingFormQueryKey, setOnboardingAccountNumber, type OnboardingFormView,
} from '../../api/onboardingForm';
import { StepUpCancelledError, StepUpUnavailableError } from '../../api/stepUp';
import { UserText } from '../../components/UserText';
import { cls as formCls } from '../../components/formStyles';
import { btns } from '../../components/buttons';
import { requestTitle, savedLine, withNumberState } from './infoRequestsModel';

/**
 * One account-number request in the "Requests from your advisor" step (docs/38 §5,
 * crossmarc/steward-app#2298). A third kind of card beside the file card and the question.
 *
 * ## Where the number lives
 *
 * While it is being typed, in THIS component's state and in the DOM input's own value. Not in the
 * form's `values` (so it is in no autosaved section payload), not in the query cache, not in
 * `localStorage` or `sessionStorage`, not in a URL, not in a log line from this file, and not on
 * the review page. It leaves the browser in the body of its own POST ({@link setOnboardingAccountNumber}).
 * That body is also held in memory by the API client (`api/client.ts`) for as long as the request
 * can be replayed after a step-up prompt (up to two retries); that copy is not this component's to
 * clear and goes when the request settles.
 *
 * What is cleared, each pinned in `AccountNumberCard.test.tsx` by a test that goes red without it:
 * a successful save (a first save, and a save made while replacing), Cancel, and the server's state
 * for the request turning saved. Leaving the step, or the card being dropped from the form, unmounts
 * it and the state goes with it (one test pins that a remount starts empty). A save that settles after
 * the card has unmounted still updates the form's cached read (`putState`); no test pins that. Two clears are NOT
 * pinned by a test and are defensive only: the one after Remove (offered only in the saved view,
 * where nothing is typed) and the one when a 404 is confirmed (the card is dropped at that moment).
 *
 * ## Leaving the step with a number typed
 *
 * The card registers a flush (`registerFlush`) that Continue, Back and a rail pick await, as the
 * SSN box does. A typed, unsaved number is saved first; if that fails, the client stays on the step
 * with the card's error and what they typed is kept. Two cases differ: when the save finds the
 * request gone (a 404 that a re-read confirms), nothing is left to stay for, so the client moves on and
 * the form shows {@link REQUEST_CLOSED_NOTE} on the step they land on; and text sitting in an open
 * Replace box is never saved by leaving (the number already saved stays as it is): the client stays,
 * with {@link NUMBER_ERRORS}.replaceOpen on that card, until they save or cancel the change. An
 * open Replace box with nothing typed does not hold the client back.
 *
 * What the card shows after a save comes from the SERVER's masked answer (`AccountNumberState`),
 * never from what was typed. The client cannot read a saved number back, so a saved number has no
 * Show; the only Show is for what is being typed. A number the FIRM entered (docs/38 §4.5) is shown
 * as the firm's, never as "Saved", and offers Replace only.
 *
 * ## Replace and the routing number
 *
 * Appcore treats a fill as a whole replace, and since crossmarc/appcore#865 (read 2026-10-13 at
 * 1461651, not pinned by a test here) it REFUSES a fill that carries no routing number while the
 * slot holds one (409 `CLIENT_ACCOUNT_NUMBER_ROUTING_HELD`, the firm's entry included); before that
 * it removed the held one. So when the request asked for a routing number, the
 * server's state says one is held, and the Replace form is open, a save needs a nine-digit routing
 * number, refused here with {@link NUMBER_ERRORS}.routingWithReplace before any request is made, and
 * the box carries {@link ROUTING_REPLACE_HINT}. The box and the saved-routing line are shown
 * whenever the request asked for routing OR the state says one is held (#2331: a request whose row
 * never asked for routing can still hold one, and the card refuses a routing-less save over it
 * itself, so the box must be enterable). If the card's state was stale and appcore refuses with that
 * 409 anyway, `save()` maps it to {@link NUMBER_ERRORS}.routingHeld, opens Replace and reads the
 * state again.
 * The rule does not apply to a first entry, nor to a request holding no routing number. It lives in
 * `save()`, so the Save button is where it bites; leaving the step never saves an open Replace box.
 *
 * ## Errors
 *
 * Mapped by status and code ({@link numberErrorMessage}), never by a server `message`, and with no
 * logging at all, so nothing typed can reach a console through this path. A 404 is checked by
 * reading the form again: only when that read succeeds and the request is gone does the card hand
 * the news to the form (`onClosed`) and clear what was typed; otherwise it shows its ordinary
 * could-not sentence and keeps what was typed. A 409 `ONBOARDING_FORM_CLOSED` reads the form again,
 * which shows the closed page when that read succeeds; when it is lost the card says the form is
 * closed. A 409 `CLIENT_ACCOUNT_NUMBER_FIRM_ENTERED` on Remove (#2340: a stale page offered Remove for a number the
 * firm has since entered) reads the form again, then says what is true of the number (see `failed()`); the next
 * Remove, Replace or Cancel click, or typing, clears it. `AccountNumberCard.test.tsx` pins the four outcomes of the
 * read (firm's, client's own, empty, read failed) and that a Replace click clears the message; the other clears are
 * not pinned. Any other failure keeps what was typed.
 *
 * The reveal-tier step-up gate on both routes is run by the API client's interceptor
 * (`api/client.ts`); a dismissed prompt arrives here as a `StepUpCancelledError`.
 */

/** Hides typed characters without making the box a password field: the same technique as the
 *  tax-id box (`OnboardingForm.tsx`, `TaxIdField`), copied rather than imported because that file
 *  imports this one's step. Where the CSS property is unsupported the box falls back to
 *  `type="password"`. */
const MASKED = '[-webkit-text-security:disc] [text-security:disc]';

function textSecuritySupported(): boolean {
  try {
    return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');
  } catch { return false; }
}

export const NUMBER_ERRORS = {
  account: 'Please check the account number and try again.',
  routing: 'Please check the routing number. That one does not match a bank routing number.',
  routingShort: 'The routing number has nine digits.',
  routingHeld: 'A routing number is already saved for this request. Please enter it again along with the account number. What you typed is still here.',
  routingWithReplace: 'Replacing this number also replaces the routing number. Please enter the routing number as well.',
  accountMissing: 'Enter the account number first.',
  conflict: 'That could not be saved just now. What you typed is still here, so you can try again.',
  rate: 'That has been tried several times in a short while. Please wait a few minutes and try again.',
  stepUp: 'We need to check that it is you before this can be saved. What you typed is still here.',
  stepUpRemove: 'We need to check that it is you before this can be removed.',
  stepUpUnavailable: 'The check that it is you is not available right now, so this could not be saved. What you typed is still here.',
  stepUpUnavailableRemove: 'The check that it is you is not available right now, so this could not be removed.',
  formClosed: 'Your form is closed, so nothing was changed.',
  replaceOpen: 'You have a change that is not saved yet. Please save it or cancel it before you continue.',
  save: 'That number could not be saved. What you typed is still here, so you can try again.',
  remove: 'That number could not be removed. Please try again.',
} as const;

/** Under the routing box while a held routing number is being replaced along with the account number. */
export const ROUTING_REPLACE_HINT = 'Saving replaces the routing number too, so please enter it again.';

/** Shown by the form, above whichever step the client is on, when a save or a remove found the request
 *  no longer open and a re-read confirmed it is gone. If a Continue, Back or rail pick raised it, the
 *  client has already moved on and sees it on the step they land on; it goes at their next move.
 *  True for both verbs, so it says nothing about what was saved. */
export const REQUEST_CLOSED_NOTE = 'One of these requests is no longer open. The list has been updated.';

/** `'closed'` for a 404 (the request may be no longer open: the card checks), `'formClosed'` for the
 *  form having closed; otherwise the sentence to show. Mapped by status and code only: the server's
 *  `message` is never read, and nothing is logged. */
export function numberErrorMessage(err: unknown, verb: 'save' | 'remove'): string | 'closed' | 'formClosed' | 'routingHeld' | 'firmEntered' {
  if (err instanceof StepUpCancelledError) return verb === 'save' ? NUMBER_ERRORS.stepUp : NUMBER_ERRORS.stepUpRemove;
  if (err instanceof StepUpUnavailableError) return verb === 'save' ? NUMBER_ERRORS.stepUpUnavailable : NUMBER_ERRORS.stepUpUnavailableRemove;
  if (isConnectivityError(err)) return userMessage(err, NUMBER_ERRORS[verb]);
  if (err instanceof ApiError) {
    if (err.status === 404) return 'closed';
    if (err.status === 409 && err.code === 'ONBOARDING_FORM_CLOSED') return 'formClosed';
    // #2340: appcore refuses a client emptying a number the firm entered (the card hides Remove for it; this is the stale-page case).
    if (verb === 'remove' && err.status === 409 && err.code === 'CLIENT_ACCOUNT_NUMBER_FIRM_ENTERED') return 'firmEntered';
    if (verb === 'save') {
      if (err.status === 400 && err.code === 'INVALID_ACCOUNT_NUMBER') return NUMBER_ERRORS.account;
      if (err.status === 400 && err.code === 'INVALID_ROUTING_NUMBER') return NUMBER_ERRORS.routing;
      if (err.status === 409 && err.code === 'CLIENT_ACCOUNT_NUMBER_ROUTING_HELD') return 'routingHeld';
      if (err.status === 409) return NUMBER_ERRORS.conflict;
      if (err.status === 429) return NUMBER_ERRORS.rate;
    }
  }
  return NUMBER_ERRORS[verb];
}

/** Under the card when a Remove was refused because the firm entered the number (#2340). */
const FIRM_ENTERED_NOTE = (firm: ReactNode): ReactNode => <>{firm} entered this number, so it cannot be removed here. You can replace it if it is not right.</>;

/** Only digits, at most nine: a routing number has no spaces or letters to keep. */
export function routingDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

/** What the card says of a number the firm entered (docs/38 §4.5: "the firm already has this number,
 *  shows the last characters, and still offers Replace"). Never "Saved": the client did not save it. */
function firmHasLine(firm: ReactNode, hint: string | undefined): ReactNode {
  return <>{firm} already has this number{hint ? `, ending in ${hint}` : ''}.</>;
}

/** Saves a typed number: `true` when nothing is left unsaved, `false` when it stays put. */
export type NumberFlush = () => Promise<boolean>;

export function AccountNumberCard({ request, state, token, firmWorkspaceId, firm, onClosed, registerFlush }: {
  request: InfoRequestForClient;
  /** The server's masked state for this request; absent reads as nothing saved. */
  state: AccountNumberState | undefined;
  token: string;
  firmWorkspaceId: string;
  /** The firm's name as it starts a sentence. */
  firm: ReactNode;
  /** The request is gone from the form (a 404 that a re-read confirmed). The form tells the client. */
  onClosed: () => void;
  /** Lets the form wait for a typed number to be saved before it leaves the step. */
  registerFlush?: (requestId: string, flush: NumberFlush | null) => void;
}) {
  const queryClient = useQueryClient();
  const [account, setAccount] = useState('');
  const [routing, setRouting] = useState('');
  const [show, setShow] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState<'saving' | 'removing' | null>(null);
  const [error, setError] = useState<ReactNode>(null);
  const [announce, setAnnounce] = useState('');
  const [cssMask] = useState(textSecuritySupported);
  const mounted = useRef(true);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const accountRef = useRef(account);
  const routingRef = useRef(routing);
  accountRef.current = account;
  routingRef.current = routing;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // The routing box follows what the server says is HELD as well as what the request asked for: a save
  // without a routing number is refused while one is held (#2331), so a held number must be enterable.
  const heldRouting = state?.filled === true && state.hasRouting;
  const wantsRouting = request.wantsRouting === true || heldRouting;
  const saved = state?.filled === true && !replacing;
  const replacingHeldRouting = wantsRouting && replacing && heldRouting;
  const byFirm = state?.filled === true && state.enteredBy === 'firm';
  const base = `onb-req-num-${request.id}`;
  const errId = `${base}-err`;
  const queryKey = onboardingFormQueryKey(firmWorkspaceId);

  /** Puts the server's state for this request in the form's cached read, which the rail, the
   *  review and the step all read. Marked stale without a refetch, as the tax-id save does. */
  function putState(next: AccountNumberState) {
    queryClient.setQueryData<OnboardingFormView>(queryKey, (old) => (old ? { ...old, accountNumbers: withNumberState(old.accountNumbers, next) } : old));
    void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
  }

  function clearTyped() {
    accountRef.current = ''; routingRef.current = '';
    setAccount(''); setRouting(''); setShow(false);
  }

  // The server may turn this request to saved while something is typed (another tab, staff entry
  // and a re-read). The boxes unmount then; what was typed must not wait behind Replace.
  useEffect(() => { if (state?.filled === true && !replacing) clearTyped(); }, [state?.filled]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Reads the form again; true only when that read worked and the request is no longer in it. */
  async function requestIsGone(): Promise<boolean> {
    try {
      await queryClient.refetchQueries({ queryKey, exact: true }, { throwOnError: true });
    } catch { return false; }
    return !queryClient.getQueryData<OnboardingFormView>(queryKey)?.requests?.some((r) => r.id === request.id);
  }

  /** What to do with a failure. Returns whether nothing typed is left unsaved (true) or it stays (false). */
  async function failed(err: unknown, verb: 'save' | 'remove'): Promise<boolean> {
    const message = numberErrorMessage(err, verb);
    if (message === 'closed') {
      if (await requestIsGone()) { clearTyped(); onClosed(); return true; }
      if (mounted.current) setError(NUMBER_ERRORS[verb]);
      return false;
    }
    if (message === 'formClosed') {
      try { await queryClient.refetchQueries({ queryKey, exact: true }, { throwOnError: true }); } catch { /* the sentence below stands */ }
      if (mounted.current) setError(NUMBER_ERRORS.formClosed);
      return false;
    }
    if (message === 'firmEntered') {
      // A stale page offered Remove for a number the firm entered. Read the form again so the card shows what is
      // true, then say what is true of it. The message is set AFTER the read so it does not sit beside a card that
      // is about to change. By what the read shows:
      //   the firm's number          -> the firm sentence (the card now shows the firm line and Replace);
      //   the client's own number    -> the ordinary remove failure (the firm sentence would be false);
      //   empty, or absent from the read -> nothing (empty boxes, or the card is gone);
      //   the read failed            -> the firm sentence, beside the card's last known state.
      // The message is state of this card and a re-read does not touch it. It goes at the next Remove, Replace or
      // Cancel click, or when the client types. So a LATER re-read (another tab's change) that empties the slot can
      // leave the firm sentence beside empty boxes until the client types; that case is not handled.
      let read = false;
      try { await queryClient.refetchQueries({ queryKey, exact: true }, { throwOnError: true }); read = true; } catch { /* the sentence stands */ }
      if (!mounted.current) return false;
      if (!read) { setError(FIRM_ENTERED_NOTE(firm)); return false; }
      const now = queryClient.getQueryData<OnboardingFormView>(queryKey)?.accountNumbers?.find((n) => n.requestId === request.id);
      // Empty, or this request absent from the read: nothing typed is left unsaved, as after a Remove that worked
      // (true). `remove()` discards this value today, so no test can observe it.
      if (now?.filled !== true) return true;
      // A filled slot with no `enteredBy` counts as the client's own.
      setError(now.enteredBy === 'firm' ? FIRM_ENTERED_NOTE(firm) : NUMBER_ERRORS.remove);
      return false;
    }
    if (message === 'routingHeld') {
      // The card did not know a routing number is held. Open the form (so what was typed stays), say so,
      // and read the form again: the state then says it is held, and the routing box shows.
      if (mounted.current) { setReplacing(true); setError(NUMBER_ERRORS.routingHeld); }
      try { await queryClient.refetchQueries({ queryKey, exact: true }, { throwOnError: true }); } catch { /* the sentence stands */ }
      return false;
    }
    if (mounted.current) setError(message);
    return false;
  }

  /** `fromFlush`: leaving the step. Nothing typed is then fine; from the Save button it is an error. */
  function save(fromFlush = false): Promise<boolean> {
    if (inFlight.current) return inFlight.current;
    const typedAccount = accountRef.current.trim();
    const typedRouting = wantsRouting ? routingRef.current.trim() : '';
    if (fromFlush && typedAccount === '' && typedRouting === '') return Promise.resolve(true);
    // Leaving never saves a replacement the client has not chosen to save: they save it or cancel it.
    if (fromFlush && state?.filled === true && replacing) { setError(NUMBER_ERRORS.replaceOpen); return Promise.resolve(false); }
    if (typedAccount === '') { setError(NUMBER_ERRORS.accountMissing); return Promise.resolve(false); }
    if (typedRouting !== '' && typedRouting.length !== 9) { setError(NUMBER_ERRORS.routingShort); return Promise.resolve(false); }
    // A save without routing over a held one is refused by appcore (409 ROUTING_HELD); refuse it here first, without a request.
    if (replacingHeldRouting && typedRouting === '') { setError(NUMBER_ERRORS.routingWithReplace); return Promise.resolve(false); }
    setBusy('saving'); setError(null);
    const run = async (): Promise<boolean> => {
      try {
        const next = await setOnboardingAccountNumber(token, firmWorkspaceId, request.id, {
          account: typedAccount, ...(typedRouting === '' ? {} : { routing: typedRouting }),
        });
        if (mounted.current) { clearTyped(); setReplacing(false); setAnnounce('Saved.'); }
        putState(next);
        return true;
      } catch (err) {
        return await failed(err, 'save');
      } finally {
        if (mounted.current) setBusy(null);
      }
    };
    const p = run();
    inFlight.current = p;
    void p.finally(() => { if (inFlight.current === p) inFlight.current = null; });
    return p;
  }

  async function remove() {
    if (inFlight.current) return;
    setBusy('removing'); setError(null);
    const run = async (): Promise<boolean> => {
      try {
        await clearOnboardingAccountNumber(token, firmWorkspaceId, request.id);
        if (mounted.current) { clearTyped(); setReplacing(false); setAnnounce('Removed.'); }
        putState({ requestId: request.id, filled: false, hasRouting: false });
        return true;
      } catch (err) {
        return await failed(err, 'remove');
      } finally {
        if (mounted.current) setBusy(null);
      }
    };
    const p = run();
    inFlight.current = p;
    void p.finally(() => { if (inFlight.current === p) inFlight.current = null; });
    await p;
  }

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!registerFlush) return;
    registerFlush(request.id, () => saveRef.current(true));
    return () => registerFlush(request.id, null);
  }, [registerFlush, request.id]);

  const fieldProps = {
    autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: false,
    'data-1p-ignore': true, 'data-lpignore': 'true', 'data-form-type': 'other',
    disabled: busy !== null,
  } as const;
  const maskClass = show ? '' : MASKED;
  const inputType = show || cssMask ? 'text' : 'password';

  return (
    <fieldset className="border border-line rounded-token p-4 mb-4" data-testid={`onb-req-${request.id}`}>
      <legend className="px-1 text-[14px] font-sans text-navy"><UserText>{requestTitle(request)}</UserText></legend>
      {saved ? (
        <div>
          <p className="m-0 text-[13px] font-sans text-navy" data-testid={`${base}-saved`}>
            {byFirm ? firmHasLine(firm, state?.accountHint) : savedLine(state?.accountHint)}
          </p>
          {wantsRouting && state?.hasRouting && (
            <p className="m-0 mt-1 text-[13px] font-sans text-navy" data-testid={`${base}-saved-routing`}>
              {byFirm
                ? <>{firm} also has the routing number{state.routingHint ? `, ending in ${state.routingHint}` : ''}.</>
                : state.routingHint ? `Routing number saved, ending in ${state.routingHint}` : 'Routing number saved'}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" className={`${btns.btnSm} max-sm:min-h-11`} disabled={busy !== null} onClick={() => { setReplacing(true); setError(null); }}>Replace</button>
            {/* docs/38 §4.5: a number the firm entered offers Replace, and no Remove. */}
            {!byFirm && <button type="button" className={`${btns.btnSm} max-sm:min-h-11`} disabled={busy !== null} onClick={() => void remove()}>Remove</button>}
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
          <div className={formCls.field}>
            <label htmlFor={`${base}-account`} className={formCls.label}>Account number</label>
            <input id={`${base}-account`} className={`${formCls.input} max-w-[260px] ${maskClass}`} type={inputType}
              inputMode="text" maxLength={64} value={account} {...fieldProps}
              aria-describedby={error ? errId : undefined} aria-invalid={error ? true : undefined}
              onChange={(e) => { setAccount(e.target.value); if (error) setError(null); }} />
          </div>
          {wantsRouting && (
            <div className={formCls.field}>
              <label htmlFor={`${base}-routing`} className={formCls.label}>Routing number</label>
              <input id={`${base}-routing`} className={`${formCls.input} max-w-[260px] ${maskClass}`} type={inputType}
                inputMode="numeric" maxLength={9} value={routing} {...fieldProps}
                aria-describedby={`${base}-routing-why${replacingHeldRouting ? ` ${base}-routing-replace` : ''}${error ? ` ${errId}` : ''}`}
                onChange={(e) => { setRouting(routingDigits(e.target.value)); if (error) setError(null); }} />
              <p id={`${base}-routing-why`} className="text-[12px] text-mid font-sans m-0 mt-1">
                {firm} uses the account number and routing number to set up transfers to or from this account. The routing number is printed at the bottom left of a check.
              </p>
              {replacingHeldRouting && (
                <p id={`${base}-routing-replace`} className="text-[12px] text-mid font-sans m-0 mt-1">{ROUTING_REPLACE_HINT}</p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btns.btnSm} disabled={busy !== null}
              aria-label={show ? 'Hide what you are typing' : 'Show what you are typing'} onClick={() => setShow((s) => !s)}>{show ? 'Hide' : 'Show'}</button>
            <button type="submit" className={btns.btnSm} disabled={busy !== null}>Save</button>
            {replacing && (
              <button type="button" className={btns.btnSm} disabled={busy !== null} onClick={() => { clearTyped(); setReplacing(false); setError(null); }}>Cancel</button>
            )}
            {busy === 'saving' && <span className="text-[12px] text-slate font-sans">Saving...</span>}
          </div>
        </form>
      )}
      {busy === 'removing' && <p className="text-[12px] text-slate font-sans m-0 mt-2">Removing...</p>}
      {error && <p id={errId} className={formCls.error} role="alert">{error}</p>}
      <p role="status" className="sr-only">{announce}</p>
    </fieldset>
  );
}
